import { CloseRounded, DensitySmallRounded } from '@mui/icons-material'
import { Box, Button, Card, Checkbox, FormControl, FormControlLabel, IconButton, Paper, Radio, RadioGroup, Skeleton, Stack, Step, StepLabel, Stepper, Typography, useTheme } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment, { Moment } from 'moment'
import { memo, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import algo from '../../core/Algo'
import { IFlow } from '../../definitions/Nodes'
import { IFlowRunConfig } from '../../definitions/NodesExecution'
import Formatter from '../../helpers/Formatter'
import { changeRoute } from '../../store/slices/SessionSlice'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import DryRunSettings from './DryRunSettings'

interface P {
    flow: Partial<IFlow>
    onStart?: (flowRunConfig?: IFlowRunConfig) => void
    startImmediately?: boolean
    setOpenStartRunModal: (openStartRunModal: boolean) => void
    runMode: 'dry' | 'full'
    isTemplate?: boolean
    setEngineConfig?: (engineConfig: IFlowRunConfig) => void
    noAnim?: boolean

}

const DATE_FORMAT = 'YYYY-MM-DD'
const NOW = moment()

const StartRunModal = ({ flow, onStart, startImmediately = false, setOpenStartRunModal, runMode, setEngineConfig, noAnim = false }: P) => {
    const dispatch = useDispatch()
    const [activeStep, setActiveStep] = useState(0)
    const [ytd, setYtd] = useState(flow.globals?.periodEnd ?? NOW.format(DATE_FORMAT))
    const [enabledCustomDate, setEnabledCustomDate] = useState(false)
    const [flowRunSetting, setFlowRunSetting] = useState<IFlowRunConfig>({
        startRunConfig: {
            engine: {
                uuid: flow?.uuid,
                ancestorUUID: flow?.versioning?.ancestorKey ?? null,
                globals: {
                    ...flow.globals,
                    periodStart: Formatter.transformDateForDataPicker(flow.globals?.periodStart, 'from'),
                    periodEnd: Formatter.transformDateForDataPicker(flow.globals?.periodEnd, 'to')
                }
            },
            settlementInstance: null,
            from: Formatter.transformDateForDataPicker(flow.globals?.periodStart, 'from'),
            to: Formatter.transformDateForDataPicker(flow.globals?.periodEnd, 'to')
        },
        dryRunConfig: runMode === 'dry' ? { samplePercentage: 100, generateOutputs: false } : null

    })
    const [selectedInstance, setSelectedInstance] = useState<number | null>(null)
    const theme = useTheme()

    const { status: contractInfoStatus, response: contractInfo } = useQuery({
        queryKey: buildQueryKey('settlements-config', flow.globals?.contractId),
        queryFn: () => ApiClient.getContractInfo(flow.globals?.contractId),
        cache: false
    })

    const handleYtdDateChange = (value: Moment | null) => {
        if (!value?.isValid()) {
            setYtd(null)
            return
        }

        if (value.isAfter(moment(), 'month')) {
            return
        }

        setYtd(value)

        const selectedYear = value.year()
        const selectedMonth = value.month()

        const fromDate = moment().year(selectedYear).month(0).date(1)

        const isCurrentMonth = selectedYear === NOW.year() && selectedMonth === NOW.month()

        const toDate = isCurrentMonth
            ? moment()
            : value.clone().endOf('month')

        setFlowRunSetting(prev => ({
            ...prev,
            startRunConfig: {
                ...prev.startRunConfig,
                from: fromDate.format(DATE_FORMAT),
                to: toDate.format(DATE_FORMAT),
                engine: {
                    ...prev.startRunConfig.engine,
                    globals: {
                        ...prev.startRunConfig.engine.globals,
                        periodStart: fromDate.format(DATE_FORMAT),
                        periodEnd: toDate.format(DATE_FORMAT)
                    }
                }
            }
        }))
    }

    const handleCustomDateChange = (value: Moment | null, period: 'from' | 'to') => {
        if (!value?.isValid()) {
            setFlowRunSetting(prev => ({
                ...prev,
                startRunConfig: {
                    ...prev.startRunConfig,
                    ...(period === 'from' ? { from: '' } : { to: '' }),
                    engine: {
                        ...prev.startRunConfig.engine,
                        globals: {
                            ...prev.startRunConfig.engine.globals,
                            ...(period === 'from' ? { periodStart: '' } : { periodEnd: '' })
                        }
                    }
                }
            }))
            return
        }
        setFlowRunSetting(prev => ({
            ...prev,
            startRunConfig: {
                ...prev.startRunConfig,
                ...(period === 'from' ? { from: value.format(DATE_FORMAT) } : { to: value.format(DATE_FORMAT) }),
                engine: {
                    ...prev.startRunConfig.engine,
                    globals: {
                        ...prev.startRunConfig.engine.globals,
                        ...(period === 'from' ? { periodStart: value.format(DATE_FORMAT) } : { periodEnd: value.format(DATE_FORMAT) })
                    }
                }
            }
        }))
    }

    useEffect(() => {
        if (contractInfoStatus === 'success') {
            setSelectedInstance(0)
            setFlowRunSetting({ ...flowRunSetting, startRunConfig: { ...flowRunSetting.startRunConfig, settlementInstance: { uuid: contractInfo.settlementConfigs[0].uuid, type: contractInfo.settlementConfigs[0].type.name, claimsRunTroughDate: contractInfo.settlementConfigs[0].claimsRunTroughDate } } })
        }
    }, [contractInfoStatus])

    const steps = runMode === 'full' ? ['Select Instance', 'Select Date'] : ['Select Instance', 'Select Date', 'Set dry-run conf']
    const hasSettlementInstance = !contractInfo?.settlementConfigs || contractInfo?.settlementConfigs.length === 0 || contractInfo?.settlementConfigs?.some(x => !algo.hasVal(x.type?.name) && !algo.hasVal(x.claimsRunTroughDate))

    return (
        <Paper sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '48vw', height: '56vh' }}>
            <Stack spacing={2} alignItems='flex-start' overflow='hidden'>
                <Stack flexDirection='row' alignItems='center' justifyContent='space-between' paddingBottom={1} width='100%' padding={2}>
                    <Stack flexDirection='row' gap={1} alignItems='center'>
                        <DensitySmallRounded />
                        <Stack flexDirection='row' alignItems='baseline' gap={1}>
                            <Typography variant='h6'>
                                Select the settlement type
                            </Typography>
                            <Typography variant='caption' color={theme => theme.palette.text.secondary}>
                                {noAnim ? 'Fast mode' : 'Normal mode'}
                            </Typography>
                        </Stack>
                    </Stack>
                    <Stack flexDirection='row'>
                        <IconButton size='small' onClick={() => setOpenStartRunModal(false)}>
                            <CloseRounded fontSize='small' />
                        </IconButton>
                    </Stack>
                </Stack>
                <Stack sx={{ backgroundColor: theme => theme.palette.background.default, width: '100%', paddingY: 2, alignItems: 'center' }}>
                    <Stepper activeStep={activeStep} sx={{ minWidth: runMode === 'dry' ? '70%' : '50%' }}>
                        {steps.map(label => (
                            <Step key={label} sx={{ '.MuiStepIcon-root.Mui-completed': { color: 'var(--color-okay)' }, '.MuiStepIcon-root.Mui-active': { color: runMode === 'dry' && theme.palette.dryRun.main } }}>
                                <StepLabel>
                                    {label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Stack>
                {activeStep === 0 && (
                    <Box display={hasSettlementInstance ? undefined : 'grid'} gridTemplateColumns='repeat(12, 1fr)' gap={2} sx={{ overflowY: 'auto', width: '100%', paddingTop: 2, padding: 2 }}>
                        {contractInfoStatus === 'loading'
                            ? (
                                    <Stack flexDirection='row' gap={2}>
                                        <Skeleton variant='rounded' width='calc(var(--unit) * 27)' height='calc(var(--unit) * 27)' />
                                        <Skeleton variant='rounded' width='calc(var(--unit) * 27)' height='calc(var(--unit) * 27)' />
                                        <Skeleton variant='rounded' width='calc(var(--unit) * 27)' height='calc(var(--unit) * 27)' />
                                    </Stack>
                                )
                            : (hasSettlementInstance || contractInfo?.settlementConfigs?.some(x => !algo.hasVal(x.type?.name) && !algo.hasVal(x.claimsRunTroughDate)))
                                    ? (
                                            <Typography color='error' variant='h6'>
                                                No settlement instance found. Please check the contract details.
                                            </Typography>
                                        )
                                    : (
                                            contractInfo?.settlementConfigs.map((x, i) => {
                                                return (
                                                    <Box key={i} gridColumn='span 4' component='div'>
                                                        <Card
                                                            sx={{
                                                                padding: 2,
                                                                borderColor: selectedInstance === i
                                                                    ? (runMode === 'dry' && theme.palette.dryRun.main) || (runMode === 'full' && 'var(--primary-color)')
                                                                    : undefined,
                                                                backgroundColor: selectedInstance === i
                                                                    ? (runMode === 'dry' && theme.palette.dryRun.main + '16') || (runMode === 'full' && 'var(--primary-selected)')
                                                                    : undefined
                                                            }}>
                                                            <Stack>
                                                                <Typography variant='subtitle2' fontWeight='700'>
                                                                    {`Settlement Instance #${i + 1}`}
                                                                </Typography>
                                                                <Typography variant='body2'>
                                                                    {x.type?.name as unknown as string}
                                                                </Typography>
                                                                <Typography variant='body1' paddingTop={2}>
                                                                    Claims run through date:
                                                                </Typography>
                                                                <Typography variant='body2' paddingBottom={2}>
                                                                    {Formatter.date(new Date(x.claimsRunTroughDate), 'mm/dd/yyyy') as unknown as string}
                                                                </Typography>
                                                                <FormControl>
                                                                    <Stack flexDirection='row' alignItems='center' justifyContent='flex-end'>
                                                                        <RadioGroup
                                                                            onChange={() => {
                                                                                setSelectedInstance(i)
                                                                                const newFlowRunSetting = structuredClone(flowRunSetting)
                                                                                newFlowRunSetting.startRunConfig.settlementInstance = {
                                                                                    uuid: contractInfo.settlementConfigs[i].uuid,
                                                                                    type: contractInfo.settlementConfigs[i].type.name,
                                                                                    claimsRunTroughDate: contractInfo.settlementConfigs[i].claimsRunTroughDate
                                                                                }
                                                                                setFlowRunSetting(newFlowRunSetting)
                                                                            }}>
                                                                            <Radio
                                                                                checked={selectedInstance === i}
                                                                                color={runMode === 'dry' ? 'dryRun' : undefined}
                                                                            />
                                                                        </RadioGroup>
                                                                    </Stack>
                                                                </FormControl>
                                                            </Stack>
                                                        </Card>
                                                    </Box>
                                                )
                                            }))}
                    </Box>
                )}
                {activeStep === 1 && (
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <Stack spacing={2} padding={2} width='70%'>
                            <Typography variant='subtitle2'>Year to Date selection</Typography>
                            <Stack flexDirection='row' alignItems='center' gap={2}>
                                <DatePicker
                                    disabled={enabledCustomDate}
                                    label='YTD'
                                    openTo='year'
                                    views={['year', 'month']}
                                    disableFuture
                                    maxDate={NOW}
                                    onChange={handleYtdDateChange}
                                    slotProps={{ textField: { size: 'small', error: ytd === null && !enabledCustomDate, InputLabelProps: { shrink: true }, color: runMode === 'dry' ? 'dryRun' : undefined } }}
                                    value={moment(ytd)}
                                />
                                <Box>
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                size='small'
                                                checked={enabledCustomDate}
                                                onChange={() => setEnabledCustomDate(prev => !prev)}
                                            />
                                        )}
                                        label='Select the precise dates instead'
                                    />
                                </Box>
                            </Stack>
                            <Typography variant='subtitle2'>Settlement&apos;s date</Typography>
                            <Stack flexDirection='row' gap={2}>
                                <DatePicker
                                    disabled={!enabledCustomDate}
                                    disableFuture
                                    maxDate={NOW}
                                    label='From'
                                    openTo='day'
                                    views={['year', 'month', 'day']}
                                    onChange={v => handleCustomDateChange(v, 'from')}
                                    slotProps={{ textField: { size: 'small', error: flowRunSetting.startRunConfig.from === '', helperText: flowRunSetting.startRunConfig.from === '' && 'Insert a valid date', InputLabelProps: { shrink: true }, color: runMode === 'dry' ? 'dryRun' : undefined } }}
                                    value={moment(flowRunSetting.startRunConfig.from, 'YYYY-MM-DD')}
                                />
                                <DatePicker
                                    disabled={!enabledCustomDate}
                                    disableFuture
                                    maxDate={NOW}
                                    label='To'
                                    openTo='day'
                                    views={['year', 'month', 'day']}
                                    onChange={v => handleCustomDateChange(v, 'to')}
                                    slotProps={{ textField: { size: 'small', error: flowRunSetting.startRunConfig.to === '', helperText: flowRunSetting.startRunConfig.to === '' && 'Insert a valid date', InputLabelProps: { shrink: true }, color: runMode === 'dry' ? 'dryRun' : undefined } }}
                                    value={moment(flowRunSetting.startRunConfig.to, 'YYYY-MM-DD')}
                                />
                            </Stack>
                        </Stack>
                    </LocalizationProvider>
                )}
                {activeStep === 2 && (
                    <DryRunSettings /* flow={globals}  */flowRunSettings={flowRunSetting} setFlowRunSettings={setFlowRunSetting} />
                )}
            </Stack>
            <Stack paddingTop={3} padding={2}>
                <Stack flexDirection='row' justifyContent='flex-end' alignItems='center' gap={1} width='100%'>
                    <Button
                        size='small'
                        variant='text'
                        color={runMode === 'dry' ? 'dryRun' : undefined}
                        disabled={activeStep === 0}
                        onClick={() => setActiveStep(prevActiveStep => prevActiveStep - 1)}
                        sx={{ mr: 1 }}>
                        Previous
                    </Button>
                    <Button
                        size='small'
                        variant='contained'
                        color={runMode === 'dry' ? 'dryRun' : undefined}
                        onClick={() => {
                            if (activeStep === steps.length - 1) {
                                if (startImmediately) {
                                    if (noAnim) {
                                        setEngineConfig(flowRunSetting)
                                        setOpenStartRunModal(false)
                                    } else {
                                        dispatch(changeRoute({ route: 'flow', params: { flowId: flow.uuid, flowRunConfig: flowRunSetting } }))
                                    }
                                } else {
                                    onStart(flowRunSetting)
                                }
                                setOpenStartRunModal(false)
                            } else {
                                setActiveStep(prev => prev + 1)
                            }
                        }}
                        disabled={(activeStep === 0 && selectedInstance == null) || hasSettlementInstance || (activeStep === 1 && (flowRunSetting.startRunConfig.from === '' || flowRunSetting.startRunConfig.to === '' || (ytd === null && !enabledCustomDate)))}>
                        {activeStep === steps.length - 1 ? (runMode === 'full' && 'Start') || (runMode === 'dry' && 'Test') : 'Select & Next'}
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    )
}

export default memo(StartRunModal)
